import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { initialComplaints } from '../data/complaints';
import { demoUsers } from '../data/roles';
import { ocrRawValues } from '../data/sampleCase';
import type { CaseFieldKey, CaseRecord, Complaint, DemoUser, RoleId } from '../types/notice';
import { toBn } from '../utils/bangla';
interface DemoContextValue {
  user: DemoUser | null;
  login: (u: DemoUser) => void;
  logout: () => void;
  switchRole: (r: RoleId) => void;
  /** number of completed workflow stages for the demo case (0–7) */
  stage: number;
  completeStage: (n: number) => void;
  resetWorkflow: () => void;
  record: CaseRecord;
  updateField: (k: CaseFieldKey, v: string) => void;
  correctedKeys: CaseFieldKey[];
  complaints: Complaint[];
  addComplaint: (c: Omit<Complaint, 'id' | 'date' | 'status' | 'assignedTo' | 'remarks'>) => Complaint;
  updateComplaint: (id: string, patch: Partial<Complaint>) => void;
}
const DemoContext = createContext<DemoContextValue | null>(null);
export function DemoProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<DemoUser | null>(null);
  const [stage, setStage] = useState(0);
  const [record, setRecord] = useState<CaseRecord>(ocrRawValues);
  const [correctedKeys, setCorrectedKeys] = useState<CaseFieldKey[]>([]);
  const [complaints, setComplaints] = useState<Complaint[]>(initialComplaints);
  const login = useCallback((u: DemoUser) => setUser(u), []);
  const logout = useCallback(() => setUser(null), []);
  const switchRole = useCallback((r: RoleId) => {
    const next = demoUsers.find(u => u.role === r);
    if (next) setUser(next);
  }, []);
  const completeStage = useCallback((n: number) => setStage(s => Math.max(s, n)), []);
  const resetWorkflow = useCallback(() => {
    setStage(0);
    setRecord(ocrRawValues);
    setCorrectedKeys([]);
  }, []);
  const updateField = useCallback((k: CaseFieldKey, v: string) => {
    setRecord(r => ({
      ...r,
      [k]: v
    }));
    setCorrectedKeys(ks => ks.includes(k) ? ks : [...ks, k]);
  }, []);
  const addComplaint: DemoContextValue['addComplaint'] = useCallback(c => {
    const nextNum = 145 + complaints.length - initialComplaints.length;
    const now = new Date();
    const created: Complaint = {
      ...c,
      id: `DMP-FR-2026-${String(nextNum).padStart(6, '0')}`,
      date: toBn(`${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`),
      status: 'নতুন',
      assignedTo: '',
      remarks: []
    };
    setComplaints(list => [created, ...list]);
    return created;
  }, [complaints.length]);
  const updateComplaint = useCallback((id: string, patch: Partial<Complaint>) => {
    setComplaints(list => list.map(c => c.id === id ? {
      ...c,
      ...patch
    } : c));
  }, []);
  const value = useMemo(() => ({
    user,
    login,
    logout,
    switchRole,
    stage,
    completeStage,
    resetWorkflow,
    record,
    updateField,
    correctedKeys,
    complaints,
    addComplaint,
    updateComplaint
  }), [user, login, logout, switchRole, stage, completeStage, resetWorkflow, record, updateField, correctedKeys, complaints, addComplaint, updateComplaint]);
  return <DemoContext.Provider value={value} data-id="element-645" data-mp="2zyj0gy1hxsnk">{children}</DemoContext.Provider>;
}
export function useDemo(): DemoContextValue {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error('useDemo must be used inside DemoProvider');
  return ctx;
}